// src/context/LocationContext.tsx
// Updated LocationContext using LocationServices for Vite + React

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useHybridData } from './HybridDataProvider';
import { LocationServices, type GeoLocation, type Vendor, type ZoningData, type BuildingCode } from '../services/LocationServices';

interface LocationContextType {
  // Core location data
  currentLocation: GeoLocation | null;
  projectAddress: string | null;
  
  // Vendor services
  nearbyVendors: Vendor[];
  vendorSearchRadius: number;
  
  // GIS/Mapping services
  zoningData: ZoningData | null;
  parcelBoundaries: any;
  elevationData: any;
  floodZone: string;
  soilType: string;
  
  // Building codes and regulations
  localBuildingCodes: BuildingCode[];
  setbackRequirements: any;
  heightRestrictions: any;
  permitRequirements: any[];
  
  // Local services
  nearbyInspectors: any[];
  materialSuppliers: any[];
  utilityProviders: any[];
  
  // Weather and environment
  weatherData: any;
  climateZone: string;
  seismicZone: string;
  windZone: string;
  
  // Loading states
  isLoading: boolean;
  error: string | null;
  
  // Functions
  updateLocation: (address: string) => Promise<void>;
  searchVendorsByTrade: (trade: string, radius?: number) => Promise<Vendor[]>;
  getLocalBuildingCodes: () => Promise<BuildingCode[]>;
  getZoningInfo: () => Promise<ZoningData | null>;
  getParcelData: () => Promise<any>;
  refreshAllLocationData: () => Promise<void>;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within LocationProvider');
  }
  return context;
};

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentProject } = useHybridData();
  
  // State for all location-based data
  const [currentLocation, setCurrentLocation] = useState<GeoLocation | null>(null);
  const [projectAddress, setProjectAddress] = useState<string | null>(null);
  const [nearbyVendors, setNearbyVendors] = useState<Vendor[]>([]);
  const [vendorSearchRadius] = useState(25); // miles
  const [zoningData, setZoningData] = useState<ZoningData | null>(null);
  const [parcelBoundaries, setParcelBoundaries] = useState<any>(null);
  const [elevationData, setElevationData] = useState<any>(null);
  const [floodZone, setFloodZone] = useState<string>('');
  const [soilType, setSoilType] = useState<string>('');
  const [localBuildingCodes, setLocalBuildingCodes] = useState<BuildingCode[]>([]);
  const [setbackRequirements] = useState<any>(null);
  const [heightRestrictions] = useState<any>(null);
  const [permitRequirements] = useState<any[]>([]);
  const [nearbyInspectors, setNearbyInspectors] = useState<any[]>([]);
  const [materialSuppliers] = useState<any[]>([]);
  const [utilityProviders] = useState<any[]>([]);
  const [weatherData, setWeatherData] = useState<any>(null);
  const [climateZone, setClimateZone] = useState<string>('');
  const [seismicZone, setSeismicZone] = useState<string>('');
  const [windZone, setWindZone] = useState<string>('');
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update all location-based data when address changes
  const updateLocation = async (address: string) => {
    console.log('Updating location context for:', address);
    setIsLoading(true);
    setError(null);
    
    try {
      const location = await LocationServices.geocodeAddress(address);
      
      if (!location) {
        throw new Error('Unable to geocode address. Please check the address and try again.');
      }
      
      setCurrentLocation(location);
      setProjectAddress(address);
      
      // Fetch all location-based data in parallel
      const [
        vendors,
        zoning,
        codes,
        inspectors,
        elevation,
        flood,
        soil,
        climate,
        seismic,
        wind,
        weather
      ] = await Promise.all([
        LocationServices.fetchNearbyVendors(location, vendorSearchRadius),
        LocationServices.fetchZoningData(location),
        LocationServices.fetchBuildingCodes(location),
        LocationServices.fetchNearbyInspectors(location),
        LocationServices.fetchElevationData(location.lat, location.lng),
        LocationServices.fetchFloodZone(location.lat, location.lng),
        LocationServices.fetchSoilData(location.lat, location.lng),
        LocationServices.fetchClimateZone(location.state, location.lat, location.lng),
        LocationServices.fetchSeismicZone(location.state, location.lat, location.lng),
        LocationServices.fetchWindZone(location.state, location.lat, location.lng),
        LocationServices.fetchWeatherData(location.lat, location.lng)
      ]);
      
      setNearbyVendors(vendors);
      setZoningData(zoning);
      setLocalBuildingCodes(codes);
      setNearbyInspectors(inspectors);
      setElevationData(elevation);
      setFloodZone(flood);
      setSoilType(soil);
      setClimateZone(climate);
      setSeismicZone(seismic);
      setWindZone(wind);
      setWeatherData(weather);
      
      // Fetch parcel boundaries if we have a parcel ID
      if (location.parcelId) {
        const boundaries = await LocationServices.fetchParcelBoundaries(
          location.parcelId,
          location.county
        );
        setParcelBoundaries(boundaries);
      }
      
      console.log('Location context updated successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update location data';
      console.error('Location update error:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Search vendors by trade
  const searchVendorsByTrade = async (trade: string, radius: number = vendorSearchRadius): Promise<Vendor[]> => {
    if (!currentLocation) {
      console.warn('No current location set');
      return [];
    }
    
    try {
      const vendors = await LocationServices.searchVendorsByTrade(
        currentLocation,
        trade,
        radius
      );
      return vendors;
    } catch (error) {
      console.error('Vendor search error:', error);
      return [];
    }
  };

  // Get local building codes
  const getLocalBuildingCodes = async (): Promise<BuildingCode[]> => {
    if (!currentLocation) {
      console.warn('No current location set');
      return [];
    }
    
    try {
      const codes = await LocationServices.fetchBuildingCodes(currentLocation);
      setLocalBuildingCodes(codes);
      return codes;
    } catch (error) {
      console.error('Building codes fetch error:', error);
      return [];
    }
  };

  // Get zoning info
  const getZoningInfo = async (): Promise<ZoningData | null> => {
    if (!currentLocation) {
      console.warn('No current location set');
      return null;
    }
    
    try {
      const zoning = await LocationServices.fetchZoningData(currentLocation);
      setZoningData(zoning);
      return zoning;
    } catch (error) {
      console.error('Zoning fetch error:', error);
      return null;
    }
  };

  // Get parcel data
  const getParcelData = async () => {
    return {
      parcelId: currentLocation?.parcelId,
      boundaries: parcelBoundaries,
      zoning: zoningData,
      elevation: elevationData,
      floodZone,
      soilType,
      location: currentLocation
    };
  };

  // Refresh all location data
  const refreshAllLocationData = async () => {
    if (projectAddress) {
      await updateLocation(projectAddress);
    } else {
      console.warn('No project address to refresh');
    }
  };

  // Watch for project changes and update location
  useEffect(() => {
    if (currentProject?.address && currentProject.address !== projectAddress) {
      updateLocation(currentProject.address);
    }
  }, [currentProject?.address]);

  // Initial load check for stored location
  useEffect(() => {
    const loadStoredLocation = async () => {
      // Check if we have a stored location in session
      const storedAddress = sessionStorage.getItem('homequest_current_address');
      if (storedAddress && !projectAddress) {
        await updateLocation(storedAddress);
      }
    };
    
    loadStoredLocation();
  }, []);

  // Store address in session when it changes
  useEffect(() => {
    if (projectAddress) {
      sessionStorage.setItem('homequest_current_address', projectAddress);
    }
  }, [projectAddress]);

  const value: LocationContextType = {
    // Core location data
    currentLocation,
    projectAddress,
    
    // Vendor services
    nearbyVendors,
    vendorSearchRadius,
    
    // GIS/Mapping services
    zoningData,
    parcelBoundaries,
    elevationData,
    floodZone,
    soilType,
    
    // Building codes and regulations
    localBuildingCodes,
    setbackRequirements,
    heightRestrictions,
    permitRequirements,
    
    // Local services
    nearbyInspectors,
    materialSuppliers,
    utilityProviders,
    
    // Weather and environment
    weatherData,
    climateZone,
    seismicZone,
    windZone,
    
    // Loading states
    isLoading,
    error,
    
    // Functions
    updateLocation,
    searchVendorsByTrade,
    getLocalBuildingCodes,
    getZoningInfo,
    getParcelData,
    refreshAllLocationData
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};